import type { FilterCardConfig } from '../models/card-model';
import type { DimensionFilterCardData } from '../models/page-filter.model';
import { resolveDropdownFilterSelectedFromData } from './card-data-gql.util';

/** Builds dropdown runtime shape from saved config when WebSocket data is not ready yet. */
export function buildPlaceholderDimensionFilterData(
	config: FilterCardConfig
): DimensionFilterCardData | undefined {
	const dim = config.data?.dimensions?.[0];
	const member = dim?.name?.trim();
	if (!member) {
		return undefined;
	}
	return {
		controlType: 'dropdown',
		dimension: {
			member,
			label: dim?.label?.trim() || member,
		},
		options: [],
		selected: [...(config.data?.selected ?? [])],
	};
}

/** Editor create flow: shell trigger before a dimension is chosen on the Data tab. */
export function buildEmptyDimensionFilterDropdownData(): DimensionFilterCardData {
	return {
		controlType: 'dropdown',
		dimension: { member: '', label: '' },
		options: [],
		selected: [],
	};
}

/** Applies saved config dimension + `data.selected` onto runtime dropdown payload. */
export function mergeDimensionFilterPreviewData(
	runtime: DimensionFilterCardData,
	config: Pick<FilterCardConfig, 'data'>
): DimensionFilterCardData {
	const configDim = config.data?.dimensions?.[0];
	const configMember = configDim?.name?.trim();
	const configLabel = configDim?.label?.trim();

	const optionValues = new Set(runtime.options.map(o => o.value));
	const allOptionValues = [...optionValues];
	const explicitSelected = config.data?.selected;
	const filteredExplicit = Array.isArray(explicitSelected)
		? explicitSelected.filter(
				v =>
					typeof v === 'string' &&
					v.trim().length > 0 &&
					optionValues.has(v)
			)
		: null;
	const selected = filteredExplicit
		? isAllOptionsSelected(filteredExplicit, allOptionValues)
			? runtime.selected.filter(v => optionValues.has(v))
			: filteredExplicit
		: (() => {
				const fromConfig =
					resolveDropdownFilterSelectedFromData(config.data) ?? [];
				const filteredFromConfig = fromConfig.filter(v =>
					optionValues.has(v)
				);
				if (filteredFromConfig.length === 0) {
					return runtime.selected;
				}
				// Same as data.selected: persisted "all" must not clobber session
				// deselection when options re-merge after getPageData / WS.
				if (
					isAllOptionsSelected(filteredFromConfig, allOptionValues)
				) {
					return runtime.selected.filter(v => optionValues.has(v));
				}
				return filteredFromConfig;
			})();
	const member =
		configMember || runtime.dimension.member?.trim() || '';
	const label =
		configLabel ||
		runtime.dimension.label?.trim() ||
		configMember ||
		member;
	return {
		...runtime,
		dimension: { member, label },
		selected,
	};
}

function isAllOptionsSelected(
	selected: readonly string[],
	allOptionValues: readonly string[]
): boolean {
	return (
		allOptionValues.length > 0 &&
		selected.length >= allOptionValues.length &&
		allOptionValues.every(value => selected.includes(value))
	);
}
